import { memo, useEffect, useState } from "react";
import { useAgentTextMotion } from "./use-agent-text-motion";
import "./agent-text-motion.css";

const WORD_REVEAL_MS = 350;
const MAX_STREAM_NODES = 128;

type StreamWord = { id: number; text: string; revealUntil: number };
type StreamView = {
  text: string;
  scope: string;
  words: StreamWord[];
  nextId: number;
  plainTail: boolean;
};

function snapshot(text: string, scope: string, nextId = 0): StreamView {
  // Keep historical text together, but isolate its unfinished trailing word.
  const tail = /\S+$/u.exec(text);
  const parts = tail ? [text.slice(0, tail.index), tail[0]] : [text];
  const words = parts
    .filter(Boolean)
    .map((part) => ({ id: nextId++, text: part, revealUntil: 0 }));
  return { text, scope, words, nextId, plainTail: false };
}

function settle(view: StreamView): StreamView {
  return {
    ...view,
    words: view.words.map((word) =>
      word.revealUntil ? { ...word, revealUntil: 0 } : word,
    ),
  };
}

function append(view: StreamView, text: string, animate: boolean): StreamView {
  if (!text.startsWith(view.text))
    return snapshot(text, view.scope, view.nextId);
  const words = [...view.words];
  const suffix = text.slice(view.text.length);
  if (view.plainTail) {
    const last = words[words.length - 1]!;
    words[words.length - 1] = { ...last, text: last.text + suffix };
    return { ...view, text, words };
  }
  let nextId = view.nextId;
  let plainTail = false;
  let continuingWord = /\S$/u.test(view.text);
  const tokens = suffix.match(/\s+|\S+/gu) ?? [];
  for (const [index, token] of tokens.entries()) {
    // Preserve old nodes/selection and keep a very long reply's tail plain.
    // This bounds DOM and retirement work even during a sustained stream.
    if (words.length >= MAX_STREAM_NODES - 1) {
      words.push({
        id: nextId++,
        text: tokens.slice(index).join(""),
        revealUntil: 0,
      });
      plainTail = true;
      break;
    }
    const whitespace = /^\s+$/u.test(token);
    if (!whitespace && continuingWord && words.length) {
      const last = words[words.length - 1]!;
      words[words.length - 1] = { ...last, text: last.text + token };
    } else {
      words.push({
        id: nextId++,
        text: token,
        revealUntil: !whitespace && animate ? Date.now() + WORD_REVEAL_MS : 0,
      });
    }
    continuingWord = !whitespace;
  }
  return { ...view, text, words, nextId, plainTail };
}

const StreamWordText = memo(function StreamWordText({
  word,
}: {
  word: StreamWord;
}) {
  return (
    <span className={word.revealUntil ? "denker-stream-word" : undefined}>
      {word.text}
    </span>
  );
});

/** Transitions.dev Streaming text on an existing plain-text reply boundary.
 * Network arrivals pace words; stable words and partial-word extensions never
 * replay. Replacements and reopening history are immediately readable. */
export function AgentStreamingText({
  text,
  scope,
  live,
}: {
  text: string;
  scope: string;
  live: boolean;
}) {
  const animate = useAgentTextMotion() && live;
  const [view, setView] = useState(() => snapshot(text, scope));
  if (view.scope !== scope) {
    setView(snapshot(text, scope, view.nextId));
  } else if (view.text !== text) {
    setView(append(view, text, animate));
  } else if (!animate && view.words.some((word) => word.revealUntil)) {
    setView(settle(view));
  }

  useEffect(() => {
    const pending = view.words.filter((word) => word.revealUntil);
    if (!pending.length) return;
    const timer = window.setTimeout(
      () => {
        const now = Date.now();
        setView((current) => ({
          ...current,
          words: current.words.map((word) =>
            word.revealUntil && word.revealUntil <= now
              ? { ...word, revealUntil: 0 }
              : word,
          ),
        }));
      },
      Math.max(
        0,
        Math.min(...pending.map((word) => word.revealUntil)) - Date.now(),
      ),
    );
    return () => window.clearTimeout(timer);
  }, [view.words]);

  return (
    <span>
      {view.words.map((word) => (
        <StreamWordText key={word.id} word={word} />
      ))}
    </span>
  );
}
